import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { saveData } from 'services/api/saveData';
import { fetchData } from 'services/api/fetchData';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';

// Storage key that keeps the running legacy export job across page reloads
const LEGACY_EXPORT_JOB_STORAGE_KEY = 'maintenance_legacy_export_job_id';

// Interval in milliseconds between two legacy export status polls
const LEGACY_EXPORT_POLL_INTERVAL_MS = 3000;

// Describes a subscription line in the missing renewal invoice preview
interface RenewalInvoiceLine {
    id: number;
    type: string;
    product_name: string | null;
    quantity: number;
    price: string | null;
    price_type: string;
    start_date: string | null;
    payment_status: string;
}

// Describes the preview of the missing renewal invoice
interface RenewalInvoicePreview {
    subscription: { id: number, package: string, origin: string, status: string, start_date: string, end_date: string };
    selected_lines: RenewalInvoiceLine[];
    excluded_lines: RenewalInvoiceLine[];
    amount: string;
    has_mandate: boolean;
    email: string | null;
    invoice_email: string | null;
    confirmation_already_sent: boolean;
    blocking_reasons: string[];
}

// Describes the result of a created missing renewal invoice
interface RenewalInvoiceResult {
    action: 'sepa_collection_started' | 'manual_invoice_created';
    invoice_number: string | null;
    amount: string;
}

// Describes the status of a running legacy export job
interface LegacyExportStatus {
    status: 'queued' | 'running' | 'finished' | 'failed';
    legacy_environment_name: string | null;
    total_endpoints: number;
    completed_endpoints: number;
    current_endpoint: string | null;
    error_message: string | null;
}

// Describes a seat line of a subscription in the subscription correction
interface CorrectionLine {
    id: number;
    type: string;
    quantity: number;
    price: string | null;
    start_date: string;
    end_date: string;
}

// Describes a subscription in the subscription correction
interface CorrectionSubscription {
    id: number;
    package: string;
    origin: string | null;
    status: string;
    duration: string;
    start_date: string;
    end_date: string;
    is_current: boolean;
    lines: CorrectionLine[];
}

// Describes an environment option of the subscription correction
interface CorrectionEnvironment {
    id: number;
    name: string;
}

// Describes the subscriptions of an environment in the subscription correction
interface CorrectionData {
    environment: { id: number, name: string };
    subscriptions: CorrectionSubscription[];
}

// Describes a dns record of the ticket inbound domain
interface InboundDnsRecord {
    record_type: string;
    priority?: string;
    value: string;
    valid: string;
}

// Describes the result of the ticket inbound domain setup
interface InboundDomainResult {
    domain: string;
    state: string | null;
    receiving_dns_records: InboundDnsRecord[];
    records_valid: boolean;
    route_in_place: boolean;
    webhook_url: string;
}

const MaintenanceActions: React.FC = () => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { handleLogout } = useAuthContext();
    const [legacyUsername, setLegacyUsername] = useState('');
    const [legacyPassword, setLegacyPassword] = useState('');
    const [legacyExportJobId, setLegacyExportJobId] = useState<string | null>(() => {
        try { return window.sessionStorage.getItem(LEGACY_EXPORT_JOB_STORAGE_KEY); } catch { return null; }
    });
    const [legacyExportStatus, setLegacyExportStatus] = useState<LegacyExportStatus | null>(null);
    const [legacyExportStarting, setLegacyExportStarting] = useState(false);
    const [legacyExportDownloading, setLegacyExportDownloading] = useState(false);
    const [legacyExportError, setLegacyExportError] = useState<string | null>(null);
    const [inboundLoading, setInboundLoading] = useState(false);
    const [inboundResult, setInboundResult] = useState<InboundDomainResult | null>(null);
    const [inboundError, setInboundError] = useState<string | null>(null);
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncResult, setSyncResult] = useState<string | null>(null);
    const [customerSyncLoading, setCustomerSyncLoading] = useState(false);
    const [customerSyncResult, setCustomerSyncResult] = useState<string | null>(null);
    const [newsletterBackfillLoading, setNewsletterBackfillLoading] = useState(false);
    const [newsletterBackfillResult, setNewsletterBackfillResult] = useState<string | null>(null);
    const [renewalEnvironmentId, setRenewalEnvironmentId] = useState<string>('5');
    const [renewalAsOfDate, setRenewalAsOfDate] = useState<string>('2026-03-01');
    const [renewalPreview, setRenewalPreview] = useState<RenewalInvoicePreview | null>(null);
    const [renewalPreviewLoading, setRenewalPreviewLoading] = useState(false);
    const [renewalCreateLoading, setRenewalCreateLoading] = useState(false);
    const [renewalResult, setRenewalResult] = useState<RenewalInvoiceResult | null>(null);
    const [renewalError, setRenewalError] = useState<string | null>(null);
    const [correctionEnvironments, setCorrectionEnvironments] = useState<CorrectionEnvironment[]>([]);
    const [correctionEnvironmentId, setCorrectionEnvironmentId] = useState<string>('');
    const [correctionData, setCorrectionData] = useState<CorrectionData | null>(null);
    const [correctionSubscriptionId, setCorrectionSubscriptionId] = useState<number | null>(null);
    const [correctionStartDate, setCorrectionStartDate] = useState<string>('');
    const [correctionEndDate, setCorrectionEndDate] = useState<string>('');
    const [correctionQuantities, setCorrectionQuantities] = useState<Record<number, string>>({});
    const [correctionFetchLoading, setCorrectionFetchLoading] = useState(false);
    const [correctionSaveLoading, setCorrectionSaveLoading] = useState(false);
    const [correctionResult, setCorrectionResult] = useState<string | null>(null);
    const [correctionError, setCorrectionError] = useState<string | null>(null);
    const [invoiceTypeEnvironmentId, setInvoiceTypeEnvironmentId] = useState<string>('');
    const [invoiceTypeLoading, setInvoiceTypeLoading] = useState(false);
    const [invoiceTypeResult, setInvoiceTypeResult] = useState<string | null>(null);
    const [invoiceTypeError, setInvoiceTypeError] = useState<string | null>(null);
    // Remember or forget the running legacy export job across page reloads
    const storeLegacyExportJobId = (jobId: string | null) => {
        setLegacyExportJobId(jobId);
        try {
            if (jobId) {
                window.sessionStorage.setItem(LEGACY_EXPORT_JOB_STORAGE_KEY, jobId);
            } else {
                window.sessionStorage.removeItem(LEGACY_EXPORT_JOB_STORAGE_KEY);
            }
        } catch {
            // Session storage is a convenience only, ignore when unavailable
        }
    };

    // Log in to the legacy platform and start the json export of the legacy environment
    const handleStartLegacyExport = async () => {
        setLegacyExportError(null);
        setLegacyExportStatus(null);

        // Require both credentials before calling the legacy platform
        if (!legacyUsername || !legacyPassword) {
            setLegacyExportError(t('maintenance.legacy_export.missing_credentials'));
            return;
        }

        setLegacyExportStarting(true);
        try {
            // Authenticate first, the token stays in the server session
            await saveData({
                apiUrl: 'authenticate_legacy_import',
                method: 'post',
                data: { username: legacyUsername, password: legacyPassword }
            });

            // Start the export job and remember its id for the polling
            const response = await saveData({ apiUrl: 'start_legacy_export', method: 'post', data: {} });
            const jobId = response?.data?.export_job_id ?? null;
            storeLegacyExportJobId(jobId);
            setLegacyExportStatus({
                status: 'queued',
                legacy_environment_name: response?.data?.legacy_environment_name ?? null,
                total_endpoints: 0,
                completed_endpoints: 0,
                current_endpoint: null,
                error_message: null,
            });
            setLegacyPassword('');
        } catch (error: any) {
            console.error(error);
            setLegacyExportError(error?.response?.data?.detail || t('maintenance.legacy_export.start_failed'));
        } finally {
            setLegacyExportStarting(false);
        }
    };

    // Poll the status of the running legacy export until it is finished or failed
    useEffect(() => {
        if (!legacyExportJobId) return;
        let cancelled = false;

        // Fetch the status of the export job once
        const pollStatus = async () => {
            try {
                const status: LegacyExportStatus | undefined = await fetchData({
                    apiUrl: `get_legacy_export_status/${legacyExportJobId}`,
                    handleLogout,
                });
                if (cancelled || !status) return;
                setLegacyExportStatus(status);

                // Stop polling once the job reached an end state
                if (status.status === 'finished' || status.status === 'failed') {
                    window.clearInterval(interval);
                }
            } catch (error: any) {
                console.error(error);

                // Forget an expired or unknown job so the form can be used again
                if (!cancelled && error?.response?.status === 404) {
                    window.clearInterval(interval);
                    storeLegacyExportJobId(null);
                    setLegacyExportStatus(null);
                }
            }
        };

        // Poll immediately and then on the interval
        const interval = window.setInterval(pollStatus, LEGACY_EXPORT_POLL_INTERVAL_MS);
        pollStatus();

        // Stop the polling when the component unmounts or the job changes
        return () => {
            cancelled = true;
            window.clearInterval(interval);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [legacyExportJobId]);

    // Download the finished legacy export zip and forget the job afterwards
    const handleDownloadLegacyExport = async () => {
        if (!legacyExportJobId || legacyExportDownloading) return;
        setLegacyExportDownloading(true);
        setLegacyExportError(null);

        try {
            // Request the zip file from the backend
            const blob = await fetchData({
                apiUrl: `download_legacy_export/${legacyExportJobId}`,
                responseAsBlob: true,
                handleLogout,
            });

            // Create a temporary link to trigger the download
            const environmentName = legacyExportStatus?.legacy_environment_name || 'legacy';
            const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/zip' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `v1_export_${environmentName}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            // The file is deleted on the server after the download, forget the job
            storeLegacyExportJobId(null);
            setLegacyExportStatus(null);
            setFloatingAlert({ type: 'success' });
        } catch (error) {
            console.error(error);
            setLegacyExportError(t('maintenance.legacy_export.download_failed'));
        } finally {
            setLegacyExportDownloading(false);
        }
    };

    // Render the status line of the running or finished legacy export
    const renderLegacyExportStatus = (status: LegacyExportStatus) => {
        const values = {
            name: status.legacy_environment_name || '-',
            completed: status.completed_endpoints,
            total: status.total_endpoints,
            current: status.current_endpoint || '-',
        };

        return (
            <div className='explanation-text'>
                <p>{t(`maintenance.legacy_export.status_${status.status}`, values)}</p>
                {status.status === 'failed' && status.error_message && (
                    <p>{status.error_message}</p>
                )}
            </div>
        );
    };

    // Trigger the sync of the customer details in the admin environment with their subscriptions
    const handleSyncAdminCustomerDetails = async () => {
        setCustomerSyncLoading(true);

        try {
            const response = await saveData({ apiUrl: 'post_sync_admin_customer_details_as_superuser', method: 'post', data: {} });
            const syncedContacts = response?.data?.synced_contacts ?? 0;
            setCustomerSyncResult(t('maintenance.billing.sync_admin_customers_result', { count: syncedContacts }));
            setFloatingAlert({ type: 'success' });
        } catch (error) {
            console.error(error);
        } finally {
            setCustomerSyncLoading(false);
        }
    };

    // Set the invoice type of every invoice of an environment from the type of its workflow
    const handleBackfillInvoiceTypes = async () => {
        setInvoiceTypeLoading(true);
        setInvoiceTypeError(null);
        setInvoiceTypeResult(null);

        try {
            const response = await saveData({
                apiUrl: 'post_backfill_invoice_types_as_superuser',
                method: 'post',
                data: { environment_id: invoiceTypeEnvironmentId }
            });
            setInvoiceTypeResult(t('maintenance.invoices.backfill_invoice_types_result', {
                proforma: response?.data?.proforma_invoices ?? 0,
                normal: response?.data?.normal_invoices ?? 0,
            }));
            setFloatingAlert({ type: 'success' });
        } catch (error: any) {
            setInvoiceTypeError(error?.response?.data?.general || t('maintenance.invoices.backfill_invoice_types_failed'));
        } finally {
            setInvoiceTypeLoading(false);
        }
    };

    // Create the email messages of the newsletter recipients sent before the email system stored them
    const handleBackfillNewsletterEmailMessages = async () => {
        setNewsletterBackfillLoading(true);

        try {
            const response = await saveData({ apiUrl: 'post_backfill_newsletter_email_messages_as_superuser', method: 'post', data: {} });
            const createdMessages = response?.data?.created_messages ?? 0;
            const skippedRecipients = response?.data?.skipped_recipients ?? 0;
            setNewsletterBackfillResult(t('maintenance.newsletters.backfill_result', { created: createdMessages, skipped: skippedRecipients }));
            setFloatingAlert({ type: 'success' });
        } catch (error) {
            console.error(error);
        } finally {
            setNewsletterBackfillLoading(false);
        }
    };

    // Trigger the sync of subscription lines with their paid invoices
    const handleSyncPaidSubscriptionLines = async () => {
        setSyncLoading(true);

        try {
            const response = await saveData({ apiUrl: 'post_sync_paid_subscription_lines_as_superuser', method: 'post', data: {} });
            const updatedLines = response?.data?.updated_lines ?? 0;
            setSyncResult(t('maintenance.billing.sync_paid_lines_result', { count: updatedLines }));
            setFloatingAlert({ type: 'success' });
        } catch (error) {
            console.error(error);
        } finally {
            setSyncLoading(false);
        }
    };

    // Register the ticket inbound domain and route in mailgun and show the dns records
    const handleSetupInboundDomain = async () => {
        setInboundLoading(true);
        setInboundError(null);

        try {
            const response = await saveData({ apiUrl: 'post_setup_ticket_inbound_domain_as_superuser', method: 'post', data: {} });
            setInboundResult(response?.data ?? null);
            setFloatingAlert({ type: 'success' });
        } catch (error: any) {
            console.error(error);
            setInboundError(error?.response?.data?.general || t('maintenance.tickets.setup_failed'));
        } finally {
            setInboundLoading(false);
        }
    };

    // Extract the readable error of a failed renewal invoice request
    const extractRenewalError = (error: any): string => {
        return error?.response?.data?.general || t('maintenance.billing.missing_renewal_invoice_failed');
    };

    // Fetch the preview of the missing renewal invoice
    const handlePreviewMissingRenewalInvoice = async () => {
        setRenewalPreviewLoading(true);
        setRenewalPreview(null);
        setRenewalResult(null);
        setRenewalError(null);

        try {
            const response = await saveData({
                apiUrl: 'post_missing_renewal_invoice_as_superuser',
                method: 'post',
                data: { environment_id: renewalEnvironmentId, as_of_date: renewalAsOfDate, dry_run: true }
            });
            setRenewalPreview(response?.data ?? null);
        } catch (error) {
            console.error(error);
            setRenewalError(extractRenewalError(error));
        } finally {
            setRenewalPreviewLoading(false);
        }
    };

    // Create and collect the missing renewal invoice after a preview without blocking reasons
    const handleCreateMissingRenewalInvoice = async () => {
        setRenewalCreateLoading(true);
        setRenewalError(null);

        try {
            const response = await saveData({
                apiUrl: 'post_missing_renewal_invoice_as_superuser',
                method: 'post',
                data: { environment_id: renewalEnvironmentId, as_of_date: renewalAsOfDate, dry_run: false }
            });
            setRenewalResult(response?.data ?? null);
            setRenewalPreview(null);
            setFloatingAlert({ type: 'success' });
        } catch (error) {
            console.error(error);
            setRenewalError(extractRenewalError(error));
        } finally {
            setRenewalCreateLoading(false);
        }
    };

    // Put the period and seat quantities of a subscription into the correction inputs
    const selectCorrectionSubscription = (subscription: CorrectionSubscription | null) => {
        setCorrectionSubscriptionId(subscription?.id ?? null);
        setCorrectionStartDate(subscription?.start_date ?? '');
        setCorrectionEndDate(subscription?.end_date ?? '');
        setCorrectionQuantities(
            Object.fromEntries((subscription?.lines ?? []).map(line => [line.id, String(line.quantity)]))
        );
    };

    // Fetch the environments with a paid subscription for the environment dropdown of the correction
    useEffect(() => {
        let cancelled = false;

        // Load the environment options once
        const loadEnvironments = async () => {
            try {
                const data: { environments: CorrectionEnvironment[] } | undefined = await fetchData({
                    apiUrl: 'get_subscription_correction_environment_list_as_superuser',
                    handleLogout,
                });
                if (!cancelled && data) setCorrectionEnvironments(data.environments);
            } catch (error) {
                console.error(error);
            }
        };
        loadEnvironments();

        // Ignore the response when the component unmounts
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch the paid subscriptions of the chosen environment for the subscription correction
    const handleFetchCorrectionSubscriptions = async (environmentId: string) => {
        setCorrectionEnvironmentId(environmentId);
        setCorrectionData(null);
        setCorrectionResult(null);
        setCorrectionError(null);
        selectCorrectionSubscription(null);
        if (!environmentId) return;
        setCorrectionFetchLoading(true);

        try {
            const data: CorrectionData | undefined = await fetchData({
                apiUrl: `get_subscription_correction_as_superuser/${environmentId}`,
                handleLogout,
            });
            if (!data) return;
            setCorrectionData(data);

            // Preselect the newest subscription of the environment
            selectCorrectionSubscription(data.subscriptions[0] ?? null);
        } catch (error: any) {
            console.error(error);
            setCorrectionError(error?.response?.data?.general || t('maintenance.billing.subscription_correction_failed'));
        } finally {
            setCorrectionFetchLoading(false);
        }
    };

    // Save the corrected period and seat quantities of the selected subscription
    const handleSaveCorrection = async () => {
        if (!correctionSubscriptionId) return;
        setCorrectionSaveLoading(true);
        setCorrectionResult(null);
        setCorrectionError(null);

        // Send the quantities of the seat lines as numbers
        const lines = Object.entries(correctionQuantities).map(([id, quantity]) => ({ id: Number(id), quantity: Number(quantity) }));

        const response = await saveData({
            apiUrl: `patch_subscription_correction_as_superuser/${correctionSubscriptionId}`,
            method: 'patch',
            data: { start_date: correctionStartDate, end_date: correctionEndDate, lines }
        }).catch((error: any) => {
            console.error(error);
            setCorrectionError(error?.response?.data?.general || t('maintenance.billing.subscription_correction_failed'));
            return undefined;
        });
        setCorrectionSaveLoading(false);
        if (!response) return;

        // Show the fresh data and the result of the corrected subscription
        const data: CorrectionData = response.data;
        setCorrectionData(data);
        const corrected = data.subscriptions.find(subscription => subscription.id === correctionSubscriptionId) ?? null;
        selectCorrectionSubscription(corrected);
        if (corrected) {
            setCorrectionResult(t('maintenance.billing.subscription_correction_result', {
                id: corrected.id,
                start: corrected.start_date,
                end: corrected.end_date,
                inList: t(corrected.is_current ? 'maintenance.billing.subscription_correction_in_list_yes' : 'maintenance.billing.subscription_correction_in_list_no'),
            }));
        }
        setFloatingAlert({ type: 'success' });
    };

    // Render the option label of a subscription in the correction select
    const renderCorrectionOption = (subscription: CorrectionSubscription) => (
        t('maintenance.billing.subscription_correction_option', {
            id: subscription.id,
            package: subscription.package,
            status: subscription.status,
            start: subscription.start_date,
            end: subscription.end_date,
            duration: subscription.duration,
        }) + ' · ' + t(subscription.is_current ? 'maintenance.billing.subscription_correction_in_list_yes' : 'maintenance.billing.subscription_correction_in_list_no')
    );

    // Render the period and seat inputs of the selected subscription in the correction
    const renderCorrectionForm = (subscription: CorrectionSubscription) => (
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <label>
                {t('maintenance.billing.subscription_correction_start_date_label')}
                <input
                    type="date"
                    value={correctionStartDate}
                    onChange={(event) => setCorrectionStartDate(event.target.value)}
                />
            </label>
            <label>
                {t('maintenance.billing.subscription_correction_end_date_label')}
                <input
                    type="date"
                    value={correctionEndDate}
                    onChange={(event) => setCorrectionEndDate(event.target.value)}
                />
            </label>
            {subscription.lines.map(line => (
                <label key={line.id}>
                    {t('maintenance.billing.subscription_correction_line_label', {
                        type: t(`maintenance.billing.subscription_correction_line_type_${line.type}`),
                        price: line.price ?? '-',
                    })}
                    <input
                        type="number"
                        min={line.type === 'seats' ? 1 : 0}
                        value={correctionQuantities[line.id] ?? ''}
                        onChange={(event) => setCorrectionQuantities(previous => ({ ...previous, [line.id]: event.target.value }))}
                    />
                </label>
            ))}
            <PrimaryButton
                onClick={handleSaveCorrection}
                label='maintenance.billing.subscription_correction_save_button'
                loading={correctionSaveLoading}
                size='medium'
            />
        </div>
    );

    // Render one subscription line of the renewal invoice preview
    const renderRenewalLine = (line: RenewalInvoiceLine) => (
        <li key={line.id}>
            {t('maintenance.billing.missing_renewal_invoice_line', {
                quantity: line.quantity,
                type: line.type,
                name: line.product_name || '',
                price: line.price ?? '-',
                price_type: line.price_type,
                start: line.start_date ?? '-',
                status: line.payment_status,
            })}
        </li>
    );

    // Render the preview of the missing renewal invoice
    const renderRenewalPreview = (preview: RenewalInvoicePreview) => {
        const blockingReasons = preview.blocking_reasons
            .map(reason => t(`maintenance.billing.missing_renewal_invoice_blocking_reasons.${reason}`))
            .join(', ');

        return (
            <div className='explanation-text'>
                <p>
                    {t('maintenance.billing.missing_renewal_invoice_subscription', {
                        id: preview.subscription.id,
                        package: preview.subscription.package,
                        origin: preview.subscription.origin,
                        status: preview.subscription.status,
                        start: preview.subscription.start_date,
                        end: preview.subscription.end_date,
                    })}
                </p>
                <p><b>{t('maintenance.billing.missing_renewal_invoice_selected_lines')}</b></p>
                {preview.selected_lines.length > 0 ? (
                    <ul>{preview.selected_lines.map(renderRenewalLine)}</ul>
                ) : (
                    <p>{t('maintenance.billing.missing_renewal_invoice_no_lines')}</p>
                )}
                <p><b>{t('maintenance.billing.missing_renewal_invoice_excluded_lines')}</b></p>
                {preview.excluded_lines.length > 0 ? (
                    <ul>{preview.excluded_lines.map(renderRenewalLine)}</ul>
                ) : (
                    <p>{t('maintenance.billing.missing_renewal_invoice_no_lines')}</p>
                )}
                <p>{t('maintenance.billing.missing_renewal_invoice_amount', { amount: preview.amount })}</p>
                <p>{t(preview.has_mandate ? 'maintenance.billing.missing_renewal_invoice_mandate_yes' : 'maintenance.billing.missing_renewal_invoice_mandate_no')}</p>
                <p>
                    {t('maintenance.billing.missing_renewal_invoice_emails', {
                        email: preview.email ?? '-',
                        invoiceEmail: preview.invoice_email || t('maintenance.billing.missing_renewal_invoice_no_invoice_email'),
                    })}
                </p>
                {preview.confirmation_already_sent && (
                    <p>{t('maintenance.billing.missing_renewal_invoice_confirmation_sent')}</p>
                )}
                {preview.blocking_reasons.length > 0 && (
                    <p>{t('maintenance.billing.missing_renewal_invoice_blocked', { reasons: blockingReasons })}</p>
                )}
            </div>
        );
    };

    return (
        <div className='container-filled'>
            <div className='section'>
                <div className='setting-item'>
                    <div>
                        <div className='title'>
                            <h5>{t('maintenance.legacy_export.label')}</h5>
                        </div>
                        <div className='content'>
                            <p className='explanation-text'>
                                {t('maintenance.legacy_export.explanation')}
                            </p>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
                                <label>
                                    {t('maintenance.legacy_export.username_label')}
                                    <input
                                        type="text"
                                        value={legacyUsername}
                                        onChange={(event) => setLegacyUsername(event.target.value)}
                                        disabled={!!legacyExportJobId}
                                    />
                                </label>
                                <label>
                                    {t('maintenance.legacy_export.password_label')}
                                    <input
                                        type="password"
                                        value={legacyPassword}
                                        onChange={(event) => setLegacyPassword(event.target.value)}
                                        disabled={!!legacyExportJobId}
                                    />
                                </label>
                                {!legacyExportJobId && (
                                    <PrimaryButton
                                        onClick={handleStartLegacyExport}
                                        label='maintenance.legacy_export.start_button'
                                        loading={legacyExportStarting}
                                        size='medium'
                                    />
                                )}
                            </div>
                            {legacyExportStatus && renderLegacyExportStatus(legacyExportStatus)}
                            {legacyExportStatus?.status === 'finished' && (
                                <PrimaryButton
                                    onClick={handleDownloadLegacyExport}
                                    label='maintenance.legacy_export.download_button'
                                    loading={legacyExportDownloading}
                                    size='medium'
                                />
                            )}
                            {legacyExportStatus?.status === 'failed' && (
                                <SecondaryButton
                                    onClick={() => { storeLegacyExportJobId(null); setLegacyExportStatus(null); }}
                                    label='maintenance.legacy_export.start_button'
                                    size='medium'
                                />
                            )}
                            {legacyExportError && (
                                <p className='explanation-text'>{legacyExportError}</p>
                            )}
                        </div>
                    </div>
                </div>
                <div className='setting-item'>
                    <div>
                        <div className='title'>
                            <h5>{t('maintenance.tickets.setup_label')}</h5>
                        </div>
                        <div className='content'>
                            <p className='explanation-text'>
                                {t('maintenance.tickets.setup_explanation')}
                            </p>
                            <PrimaryButton
                                onClick={handleSetupInboundDomain}
                                label='maintenance.tickets.setup_button'
                                loading={inboundLoading}
                                size='medium'
                            />
                            {inboundResult && (
                                <div className='explanation-text'>
                                    <p>{t('maintenance.tickets.setup_domain', { domain: inboundResult.domain, state: inboundResult.state ?? '-' })}</p>
                                    <p>{t(inboundResult.route_in_place ? 'maintenance.tickets.setup_route_ok' : 'maintenance.tickets.setup_route_failed', { url: inboundResult.webhook_url })}</p>
                                    <p>{t(inboundResult.records_valid ? 'maintenance.tickets.setup_records_valid' : 'maintenance.tickets.setup_records_pending')}</p>
                                    <p><b>{t('maintenance.tickets.setup_records_label')}</b></p>
                                    <ul>
                                        {inboundResult.receiving_dns_records.map((record, index) => (
                                            <li key={index}>
                                                {record.record_type} {record.priority ? `(${t('maintenance.tickets.setup_priority')} ${record.priority}) ` : ''}{record.value} · {record.valid}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {inboundError && (
                                <p className='explanation-text'>{inboundError}</p>
                            )}
                        </div>
                    </div>
                </div>
                <div className='setting-item'>
                    <div>
                        <div className='title'>
                            <h5>{t('maintenance.billing.sync_paid_lines_label')}</h5>
                        </div>
                        <div className='content'>
                            <p className='explanation-text'>
                                {t('maintenance.billing.sync_paid_lines_explanation')}
                            </p>
                            <PrimaryButton
                                onClick={handleSyncPaidSubscriptionLines}
                                label='maintenance.billing.sync_paid_lines_button'
                                loading={syncLoading}
                                size='medium'
                            />
                            {syncResult && (
                                <p className='explanation-text'>
                                    {syncResult}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                <div className='setting-item'>
                    <div>
                        <div className='title'>
                            <h5>{t('maintenance.billing.sync_admin_customers_label')}</h5>
                        </div>
                        <div className='content'>
                            <p className='explanation-text'>
                                {t('maintenance.billing.sync_admin_customers_explanation')}
                            </p>
                            <PrimaryButton
                                onClick={handleSyncAdminCustomerDetails}
                                label='maintenance.billing.sync_admin_customers_button'
                                loading={customerSyncLoading}
                                size='medium'
                            />
                            {customerSyncResult && (
                                <p className='explanation-text'>
                                    {customerSyncResult}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                <div className='setting-item'>
                    <div>
                        <div className='title'>
                            <h5>{t('maintenance.newsletters.backfill_label')}</h5>
                        </div>
                        <div className='content'>
                            <p className='explanation-text'>
                                {t('maintenance.newsletters.backfill_explanation')}
                            </p>
                            <PrimaryButton
                                onClick={handleBackfillNewsletterEmailMessages}
                                label='maintenance.newsletters.backfill_button'
                                loading={newsletterBackfillLoading}
                                size='medium'
                            />
                            {newsletterBackfillResult && (
                                <p className='explanation-text'>
                                    {newsletterBackfillResult}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                <div className='setting-item'>
                    <div>
                        <div className='title'>
                            <h5>{t('maintenance.invoices.backfill_invoice_types_label')}</h5>
                        </div>
                        <div className='content'>
                            <p className='explanation-text'>
                                {t('maintenance.invoices.backfill_invoice_types_explanation')}
                            </p>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
                                <label>
                                    {t('maintenance.invoices.backfill_invoice_types_environment_label')}
                                    <input
                                        type="number"
                                        value={invoiceTypeEnvironmentId}
                                        onChange={(event) => setInvoiceTypeEnvironmentId(event.target.value)}
                                    />
                                </label>
                                <PrimaryButton
                                    onClick={handleBackfillInvoiceTypes}
                                    label='maintenance.invoices.backfill_invoice_types_button'
                                    loading={invoiceTypeLoading}
                                    size='medium'
                                />
                            </div>
                            {invoiceTypeResult && (
                                <p className='explanation-text'>
                                    {invoiceTypeResult}
                                </p>
                            )}
                            {invoiceTypeError && (
                                <p className='explanation-text error-message'>
                                    {invoiceTypeError}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                <div className='setting-item'>
                    <div>
                        <div className='title'>
                            <h5>{t('maintenance.billing.missing_renewal_invoice_label')}</h5>
                        </div>
                        <div className='content'>
                            <p className='explanation-text'>
                                {t('maintenance.billing.missing_renewal_invoice_explanation')}
                            </p>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
                                <label>
                                    {t('maintenance.billing.missing_renewal_invoice_environment_label')}
                                    <input
                                        type="number"
                                        value={renewalEnvironmentId}
                                        onChange={(event) => setRenewalEnvironmentId(event.target.value)}
                                    />
                                </label>
                                <label>
                                    {t('maintenance.billing.missing_renewal_invoice_date_label')}
                                    <input
                                        type="date"
                                        value={renewalAsOfDate}
                                        onChange={(event) => setRenewalAsOfDate(event.target.value)}
                                    />
                                </label>
                                <SecondaryButton
                                    onClick={handlePreviewMissingRenewalInvoice}
                                    label='maintenance.billing.missing_renewal_invoice_preview_button'
                                    loading={renewalPreviewLoading}
                                    size='medium'
                                />
                            </div>
                            {renewalPreview && renderRenewalPreview(renewalPreview)}
                            {renewalPreview && renewalPreview.blocking_reasons.length === 0 && (
                                <PrimaryButton
                                    onClick={handleCreateMissingRenewalInvoice}
                                    label='maintenance.billing.missing_renewal_invoice_create_button'
                                    loading={renewalCreateLoading}
                                    size='medium'
                                />
                            )}
                            {renewalResult && (
                                <p className='explanation-text'>
                                    {t(renewalResult.action === 'sepa_collection_started'
                                        ? 'maintenance.billing.missing_renewal_invoice_result_sepa'
                                        : 'maintenance.billing.missing_renewal_invoice_result_manual',
                                    { number: renewalResult.invoice_number ?? '-', amount: renewalResult.amount })}
                                </p>
                            )}
                            {renewalError && (
                                <p className='explanation-text'>
                                    {renewalError}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                <div className='setting-item'>
                    <div>
                        <div className='title'>
                            <h5>{t('maintenance.billing.subscription_correction_label')}</h5>
                        </div>
                        <div className='content'>
                            <p className='explanation-text'>
                                {t('maintenance.billing.subscription_correction_explanation')}
                            </p>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
                                <label>
                                    {t('maintenance.billing.subscription_correction_environment_label')}
                                    <select
                                        value={correctionEnvironmentId}
                                        disabled={correctionFetchLoading}
                                        onChange={(event) => handleFetchCorrectionSubscriptions(event.target.value)}>
                                        <option value=''>{t('maintenance.billing.subscription_correction_environment_placeholder')}</option>
                                        {correctionEnvironments.map(environment => (
                                            <option key={environment.id} value={environment.id}>
                                                {environment.name} (#{environment.id})
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                            {correctionData && correctionData.subscriptions.length === 0 && (
                                <p className='explanation-text'>{t('maintenance.billing.subscription_correction_no_subscriptions')}</p>
                            )}
                            {correctionData && correctionData.subscriptions.length > 0 && (
                                <div style={{ marginBottom: '1rem' }}>
                                    <label>
                                        {t('maintenance.billing.subscription_correction_subscription_label')} ({correctionData.environment.name})
                                        <select
                                            value={correctionSubscriptionId ?? ''}
                                            onChange={(event) => selectCorrectionSubscription(
                                                correctionData.subscriptions.find(subscription => subscription.id === Number(event.target.value)) ?? null
                                            )}>
                                            {correctionData.subscriptions.map(subscription => (
                                                <option key={subscription.id} value={subscription.id}>
                                                    {renderCorrectionOption(subscription)}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <p className='explanation-text'>{t('maintenance.billing.subscription_correction_line_helperText')}</p>
                                </div>
                            )}
                            {correctionData && correctionSubscriptionId && (() => {
                                const selected = correctionData.subscriptions.find(subscription => subscription.id === correctionSubscriptionId);
                                return selected ? renderCorrectionForm(selected) : null;
                            })()}
                            {correctionResult && (
                                <p className='explanation-text'>
                                    {correctionResult}
                                </p>
                            )}
                            {correctionError && (
                                <p className='explanation-text'>
                                    {correctionError}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MaintenanceActions;
